import styles from './PickUpCompleted.module.scss';
import Avatar from '@components/Avatar';
import { useEffect, useState, useMemo } from 'react';
import { Button, ButtonType, ButtonSize } from "@components/Button";
import { Text, TextColor, TextSize, TextType, TextWeight } from "@components/Text";
import { Column, Row } from "@components/Grid";
import Box from "@components/Box";
import { TabsList } from "@components/Tabs/Tabs";
import Table from '@components/Table/Table';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes';
import { ErrorResponse } from '@api/Responses/ErrorResponse';
import {
  getOneTimePickUp,
  PickUpOrder,
  PickUpOrderParcel,
  PickUpType,
} from '@api/apiClient/pickUp';
import { Seller } from '@api/apiClient';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { formatDate } from "@utils/formatValues";
import { IPickUpCompletedProps } from '../../types';

const PickUpCompleted: React.FC<IPickUpCompletedProps> = (props: IPickUpCompletedProps): React.ReactElement => {
  const {
    pickUpOrder: {
      address: {
        rawLine
      },
      contactName,
      contactPhone,
      id,
      sellers,
      shipmentDate,
    }
  } = props;

  const [areAllShopsVisible, setAreAllShopsVisible] = useState<boolean>(false);
  const [shopsListHeight, setShopsListHeight] = useState<string>('');
  const [tab, setTab] = useState<string>("paid");
  const [showAll, setShowAll] = useState<boolean>(false);

  const { t } = useTranslation();

  const shopsListItemMB = 15;

  useEffect(() => {
    const shopsListItemHeight = document.querySelector(`.${styles.shopsListItem}`)?.clientHeight || 0;
    const limitListItems = 4;
    const desiredHeight = areAllShopsVisible || sellers.length < limitListItems
        ? 'auto'
        : (limitListItems * shopsListItemHeight + ((limitListItems - 1) * shopsListItemMB)) + 'px';

    setShopsListHeight(desiredHeight);
  }, [areAllShopsVisible]);

  const query = useQuery<PickUpOrder, ErrorResponse>({
    queryKey: ['pickUpParcels', id],
    queryFn: () => getOneTimePickUp(id),
    retry: 1
  });

  const columns: TableColumn<PickUpOrderParcel>[] = [
    {
      header: "",
      accessor: (row: TableRowType<PickUpOrderParcel>) => (
        <>
          <Text
            color={TextColor.PRIMARY}
            onHoverColor={TextColor.BLUE}
            size={TextSize.S}
            weight={TextWeight.BOLD}
          >
            {row?.data?.trackNumber}
          </Text>

          <Text
            color={TextColor.DARK_MUTED}
            size={TextSize.XS}
            type={TextType.BLOCK}
          >
            {row?.data?.externalNumber}
          </Text>
        </>
      ),
      fixWidth: true,
      noOverflow: true,
      width: '20%'
    },
    {
      header: "",
      accessor: (row: TableRowType<PickUpOrderParcel>) => (
          <div className={styles.store}>
            <Avatar
                id={row.data.sellerId}
                name={row.data.sellerName}
                type="small"
            />
            {row.data.sellerName}
          </div>
      ),
      fixWidth: true,
      noOverflow: true,
      type: TableColumnType.TERTIARY,
      width: '45%'
    },
    {
      header: "",
      accessor: (row: TableRowType<PickUpOrderParcel>) => (
        <Text size={TextSize.M} color={TextColor.DARK_MUTED}>
          {formatDate(row.data.createdAt, "eee, dd.MM")}
        </Text>
      ),
      fixWidth: true,
      noOverflow: true,
      width: '15%'
    }
  ];

  const parcels = query?.data?.parcels || ([] as PickUpOrderParcel[]);
  const { freeParcels, paidParcels } = useMemo(() => {
    return parcels.reduce((acc, v) => {
      if (v.pickUpType === PickUpType.Paid) { acc.paidParcels.push(v) }
      else if (v.pickUpType === PickUpType.Free) { acc.freeParcels.push(v) }
      return acc;
    }, { freeParcels: [], paidParcels: [] } as { freeParcels: PickUpOrderParcel[], paidParcels: PickUpOrderParcel[] });
  }, [parcels]);

  const moreCount = (tab === "free")
    ? (freeParcels?.length > 8 ? (freeParcels.length - 8) : 0)
    : (paidParcels?.length > 8 ? (paidParcels.length - 8) : 0);

  useEffect(() => {
    if (!paidParcels.length && freeParcels.length) {
      setTab("free")
    }
  }, [paidParcels.length, freeParcels.length])

  return (
      <div>
        <div className={styles.row}>
          <span className={styles.title}>
            {t('pickUpCompleted.options')}
          </span>
        </div>
        <div className={styles.row}>
          <div className={styles.info}>
            <div className={styles.point}>
              <div className={styles.pointTitle}>
                {t('pickUpCompleted.address')}
              </div>
              <div className={styles.pointText}>
                {rawLine}
              </div>
            </div>
            <div className={styles.point}>
              <div className={styles.pointTitle}>
                {t('pickUpCompleted.dateAndTimePickUp')}
              </div>
              <div className={styles.pointText}>
                {formatDate(shipmentDate,'dd.MM')}, 09:00-18:00
              </div>
            </div>
            <div className={styles.point}>
              <div className={styles.pointTitle}>
                {t('pickUpCompleted.shops')}
              </div>
              <div className={styles.shops}>
                <ul
                    className={`${styles.shopsList} ${areAllShopsVisible ? '' : styles.shopsListHidden}`}
                    style={{ height: shopsListHeight }}
                >
                  {sellers.map((seller: Seller, index: number) => (
                      <li
                          className={styles.shopsListItem}
                          key={seller.id}
                          style={{ marginBottom: (index !== sellers.length - 1) ? shopsListItemMB + 'px' : '' }}
                      >
                        <Avatar
                            className={styles.shopsListItemIcon}
                            id={seller.id}
                            name={seller.name}
                            type="small"
                        />
                        {seller.name}
                      </li>
                  ))}
                </ul>
                {sellers.length > 4 && (
                    <button
                        className={styles.shopsAction}
                        onClick={() => setAreAllShopsVisible(!areAllShopsVisible)}
                    >
                      {areAllShopsVisible ? t('pickUpCompleted.hide') : `+ ${t('pickUpCompleted.more')}`}
                    </button>
                )}
              </div>
            </div>
            <div className={styles.point}>
              <div className={styles.pointTitle}>
                {t('pickUpCompleted.contactPerson')}
              </div>
              <div className={styles.pointText}>
                {contactName}
              </div>
              <div className={styles.pointText}>
                {contactPhone}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.row}>
          <span className={styles.title}>
            {t('pickUpCompleted.parcels')}
          </span>
        </div>

        <TabsList
          tabs={[
            {
              id: "paid",
              title: t("pickupOrder.paid"),
              content: null,
              count: paidParcels.length,
              disable: !paidParcels.length,
            },
            {
              id: "free",
              title: t("pickupOrder.free"),
              content: null,
              count: freeParcels.length,
              disable: !freeParcels.length,
            },
          ]}
          onChange={(v) => setTab(v)}
          value={tab}
        />

        <div className={styles.row}>
          <Table<PickUpOrderParcel>
              columns={columns}
              data={(tab === "free")
                ? (showAll ? freeParcels : freeParcels.slice(0, 8))
                : (showAll ? paidParcels : paidParcels.slice(0, 8))
              }
              isHeadless
          />
        </div>

        {(moreCount && !showAll) ? (
          <Row>
            <Column phone={12}>
              <Box pt={3}>
                <Button
                  type={ButtonType.GREY_TEXT}
                  onClick={() => setShowAll(true)}
                  title={t("pickupOrder.more", { count: moreCount })}
                  size={ButtonSize.S}
                  isWide
                />
              </Box>
            </Column>
          </Row>
        ) : null}
      </div>
  );
};

export default PickUpCompleted;
